import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Search, UserPlus } from 'lucide-react';
import { CAPABILITIES, CAPABILITY_LABELS, ROLES, ROLE_DESCRIPTIONS, ROLE_LABELS, can } from '@rundown/policy';
import { Button, EmptyState, Pill } from '../../../components';
import { useVendorName } from '../../workspace/useWorkspace';
import { cx } from '../../../lib/cx';
import { useAuth } from '../../auth/useAuth';
import { membershipsQueryOptions } from '../api';
import { hasBilling, type Membership } from '../types';
import { GRADE_LABELS } from './people/shared';
import { AddPersonDialog } from './people/AddPersonDialog';
import { ManagePersonDialog } from './people/ManagePersonDialog';
import { Avatar, StatusPill, isClientRole } from './people/shared';

/**
 * Who has a seat, what they can do with it, and how they get in.
 *
 * The list is for reading; everything that changes a person happens in one
 * dialog opened from their row. Noorix creates every account (there is no
 * self sign-up — see identity.service.ts), so "Add person" creates the account
 * and, optionally, its password in the same step.
 *
 * Two ideas stay deliberately apart inside that dialog, because they look alike
 * and are not: removing someone from THIS workspace (their membership), and
 * suspending their account, which reaches every workspace they belong to.
 */

function PersonRow({
  membership,
  isSelf,
  onOpen,
}: {
  membership: Membership;
  isSelf: boolean;
  onOpen: () => void;
}) {
  const dimmed = !membership.active || membership.accountSuspended;
  // Absent (no `billing.read`) and null (no grade set) both mean "nothing to
  // show" on a row — see the Membership type for why they are distinguishable
  // at all.
  const grade = hasBilling(membership) ? membership.grade : null;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center gap-3 border-b border-line px-4 py-3 text-left last:border-b-0 hover:bg-surface"
    >
      <span className={cx(dimmed && 'opacity-50')}>
        <Avatar name={membership.name} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className={cx('truncate fs-13 font-semibold', dimmed ? 'text-muted' : 'text-ink')}>
            {membership.name}
          </span>
          {isSelf && <Pill tone="accent">You</Pill>}
        </span>
        <span className="block truncate fs-12 text-muted">
          {membership.email}
          <span className="sm:hidden"> · {ROLE_LABELS[membership.role]}</span>
          {/* Only for the people it means something about. A client owner has
              no grade because the question does not apply to them, and showing
              "—" for it would imply it had been left unset. */}
          {!isClientRole(membership.role) && (
            <>
              {grade !== null && <> · {GRADE_LABELS[grade]}</>}
              {membership.allocatedHours > 0 && <> · {membership.allocatedHours}h/wk here</>}
            </>
          )}
        </span>
      </span>

      <span className="hidden w-[9.375rem] flex-none truncate fs-12.5 text-ink-soft sm:block">
        {ROLE_LABELS[membership.role]}
      </span>

      <span className="flex w-[4.75rem] flex-none justify-end">
        <StatusPill membership={membership} />
      </span>

      <ChevronRight size={16} className="flex-none text-faint group-hover:text-ink" />
    </button>
  );
}

function RosterGroup({
  title,
  people,
  selfUserId,
  onOpen,
}: {
  title: string;
  people: readonly Membership[];
  selfUserId: string | undefined;
  onOpen: (membership: Membership) => void;
}) {
  if (people.length === 0) return null;
  return (
    <section className="overflow-hidden rounded-xl border border-line bg-base">
      <header className="flex items-center justify-between border-b border-line bg-surface px-4 py-2">
        <h3 className="fs-11 font-semibold tracking-wide text-muted uppercase">{title}</h3>
        <span className="fs-11 text-faint">{people.length}</span>
      </header>
      {people.map((membership) => (
        <PersonRow
          key={membership.id}
          membership={membership}
          isSelf={membership.userId === selfUserId}
          onOpen={() => onOpen(membership)}
        />
      ))}
    </section>
  );
}

/**
 * What each role actually grants, derived from the policy matrix rather than
 * written out beside it, so it cannot drift from what the server enforces.
 * `CAPABILITIES.filter` rather than a Set, so the list keeps a stable order.
 */
function RoleGuide() {
  return (
    <details className="group rounded-xl border border-line bg-base">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
        <span>
          <span className="block fs-13 font-semibold text-ink">What each role can do</span>
          <span className="block fs-12 text-muted">Read from the same rules the server enforces.</span>
        </span>
        <ChevronRight size={16} className="flex-none text-faint transition-transform group-open:rotate-90" />
      </summary>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(13.75rem,1fr))] gap-3 border-t border-line p-4">
        {ROLES.map((role) => (
          <div key={role} className="rounded-lg border border-line bg-surface px-3 py-2.5">
            <h4 className="fs-12.5 font-semibold text-ink">{ROLE_LABELS[role]}</h4>
            <p className="mt-0.5 fs-11.5 text-muted">{ROLE_DESCRIPTIONS[role]}</p>
            <ul className="mt-2 flex flex-col gap-0.5">
              {CAPABILITIES.filter((capability) => can(role, capability)).map((capability) => (
                <li key={capability} className="fs-11.5 text-ink-soft">
                  · {CAPABILITY_LABELS[capability]}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </details>
  );
}

const statusRank = (m: Membership) => (m.accountSuspended || !m.active ? 1 : 0);

/** Active people first, then removed or suspended; alphabetical within each. */
function byStatusThenName(a: Membership, b: Membership): number {
  return statusRank(a) - statusRank(b) || a.name.localeCompare(b.name);
}

export function PeopleSection({ workspaceId }: { workspaceId: string }) {
  const vendorName = useVendorName();
  const query = useQuery(membershipsQueryOptions(workspaceId));
  const { profile } = useAuth();
  const selfUserId = profile?.user.id;

  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  // By id, not by object, so the dialog always shows the freshest row after a write refetches the list.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { clients, team } = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matches = (query.data ?? [])
      .filter(
        (m) =>
          needle === '' || m.name.toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle),
      )
      // eslint-disable-next-line unicorn/no-array-sort -- sorting the array `filter` just built
      .sort(byStatusThenName);
    return {
      clients: matches.filter((m) => isClientRole(m.role)),
      team: matches.filter((m) => !isClientRole(m.role)),
    };
  }, [query.data, search]);

  const selected = query.data?.find((m) => m.id === selectedId) ?? null;
  const total = query.data?.length ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1 basis-[12.5rem]">
          <h2 className="fs-15 font-semibold text-ink">
            People {total > 0 && <span className="font-normal text-muted">· {total}</span>}
          </h2>
          <p className="fs-12 text-muted">Click someone to change their role, password or access.</p>
        </div>

        {total > 0 && (
          <label className="relative flex-1 basis-[12.5rem] sm:max-w-[15rem]">
            <Search
              size={14}
              className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-faint"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name or email"
              aria-label="Search people"
              className="h-8 w-full rounded-md border border-line-stronger bg-base pr-2.5 pl-8 fs-13 text-ink placeholder:text-faint focus:border-accent focus:outline-none"
            />
          </label>
        )}

        <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
          <UserPlus size={14} />
          Add person
        </Button>
      </div>

      {query.isPending ? (
        <div className="rounded-xl border border-line bg-base px-4 py-8 text-center fs-12 text-muted">
          Loading people…
        </div>
      ) : query.error !== null ? (
        <div className="rounded-xl border border-line bg-base">
          <EmptyState title="Couldn't load people" description={query.error.message} />
        </div>
      ) : total === 0 ? (
        <div className="rounded-xl border border-line bg-base">
          <EmptyState
            title="Nobody here yet"
            description="Add the first person to give them access to this workspace."
            action={
              <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
                <UserPlus size={14} />
                Add person
              </Button>
            }
          />
        </div>
      ) : clients.length === 0 && team.length === 0 ? (
        <div className="rounded-xl border border-line bg-base">
          <EmptyState
            title="No matches"
            description={`Nobody's name or email contains "${search.trim()}".`}
          />
        </div>
      ) : (
        <>
          <RosterGroup
            title="Client"
            people={clients}
            selfUserId={selfUserId}
            onOpen={(m) => setSelectedId(m.id)}
          />
          <RosterGroup
            title={`${vendorName} team`}
            people={team}
            selfUserId={selfUserId}
            onOpen={(m) => setSelectedId(m.id)}
          />
        </>
      )}

      <RoleGuide />

      <AddPersonDialog workspaceId={workspaceId} open={adding} onOpenChange={setAdding} />

      {selected !== null && (
        <ManagePersonDialog
          key={selected.id}
          workspaceId={workspaceId}
          membership={selected}
          isSelf={selected.userId === selfUserId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
